import {
  Alert,
  BULK_EDIT_UPDATE_EMPTY_WARNING,
  Card,
  Checkbox,
  Grid,
  GridBreak,
  pn,
  Stack,
  useElementBreakpoints,
  ValidationErrors,
} from "@compass/core";
import { DatePicker, DurationInput, Input, Select } from "components";
import {
  StaffServiceBulkUpdate,
  StaffServiceBulkUpdateResource,
  StaffServiceBulkUpdateSchema,
} from "generated/openApi";
import ApiService from "services/apiService";
import { ServiceFiles } from "../ServiceFiles";

export const ServiceUpdateSchema = (props: {
  updateSchema: StaffServiceBulkUpdateSchema;
  setUpdateSchema: (value: StaffServiceBulkUpdateSchema) => void;
  resource: StaffServiceBulkUpdateResource;
  validationErrors: ValidationErrors | string[];
}) => {
  const { breakpointsRef, smallFieldBreakpoints, largeFieldBreakpoints, fullFieldBreakpoints } =
    useElementBreakpoints();

  const properties =
    props.resource.properties?.find((x) => x.propertyName === pn<StaffServiceBulkUpdate>("updateSchema"))?.children ||
    [];

  // const selectedServiceType = props.updateSchema.updateServiceTypeId
  //   ? props.resource.serviceTypes?.find((x) => x.id === props.updateSchema.serviceTypeId)
  //   : undefined;

  const dateField = () => {
    const propertyName = pn<StaffServiceBulkUpdateSchema>("date");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateDate;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateDate: e.target.checked })}
        />

        <DatePicker
          modelProperty={property}
          required={isUpdating}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.date}
          onChange={(value) => props.setUpdateSchema({ ...props.updateSchema, date: value })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const serviceTypeField = () => {
    const propertyName = pn<StaffServiceBulkUpdateSchema>("serviceTypeId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateServiceTypeId;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateServiceTypeId: e.target.checked })}
        />

        <Select
          modelProperty={property}
          required={isUpdating}
          errors={props.validationErrors[propertyName]}
          value={props.resource.serviceTypes?.find((x) => x.id === props.updateSchema.serviceTypeId)}
          options={props.resource.serviceTypes}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) =>
            props.setUpdateSchema({
              ...props.updateSchema,
              serviceTypeId: value?.id,
            })
          }
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const nameField = () => {
    const propertyName = pn<StaffServiceBulkUpdateSchema>("name");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateName;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateName: e.target.checked })}
        />
        <Select
          freeSolo
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          inputValue={props.updateSchema.name}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, name: value?.name })}
          onInputChange={(e) => props.setUpdateSchema({ ...props.updateSchema, name: e.target.value })}
          fetchFunction={(searchString: string) => ApiService.search.staffServiceNames(searchString)}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const durationInMinutesField = () => {
    const propertyName = pn<StaffServiceBulkUpdateSchema>("durationInMinutes");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateDurationInMinutes;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateDurationInMinutes: e.target.checked })}
        />
        <DurationInput
          label={property?.displayName}
          // required={isUpdating}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.durationInMinutes}
          onChange={(value) => props.setUpdateSchema({ ...props.updateSchema, durationInMinutes: value })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const schoolField = () => {
    const propertyName = pn<StaffServiceBulkUpdateSchema>("schoolId");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateSchoolId;
    const required = isUpdating;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateSchoolId: e.target.checked })}
        />
        <Select
          modelProperty={property}
          required={required}
          errors={props.validationErrors[propertyName]}
          value={props.resource.schools?.find((x) => x.id === props.updateSchema.schoolId)}
          options={props.resource.schools}
          getOptionLabel={(option) => option.name}
          onChange={(e, value) => props.setUpdateSchema({ ...props.updateSchema, schoolId: value?.id })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  const noteField = () => {
    const propertyName = pn<StaffServiceBulkUpdateSchema>("note");
    const property = properties.find((x) => x.propertyName === propertyName);
    const isUpdating = props.updateSchema.updateNote;

    return (
      <Stack direction="row" alignItems="center" spacing="sm">
        <Checkbox
          checked={isUpdating}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, updateNote: e.target.checked })}
        />
        <Input
          multiline
          modelProperty={property}
          errors={props.validationErrors[propertyName]}
          value={props.updateSchema.note}
          onChange={(e) => props.setUpdateSchema({ ...props.updateSchema, note: e.target.value })}
          disabled={!isUpdating}
        />
      </Stack>
    );
  };

  return (
    <Card header="Updating Fields">
      <Stack spacing="md">
        <Alert severity="warning" variant="standard">
          {BULK_EDIT_UPDATE_EMPTY_WARNING}
        </Alert>

        <Grid ref={breakpointsRef} container spacing="sm">
          <Grid item xs={smallFieldBreakpoints}>
            {dateField()}
          </Grid>

          <GridBreak />

          <Grid item xs={smallFieldBreakpoints}>
            {serviceTypeField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {nameField()}
          </Grid>
          <Grid item xs={smallFieldBreakpoints}>
            {durationInMinutesField()}
          </Grid>

          <Grid item xs={smallFieldBreakpoints}>
            {schoolField()}
          </Grid>

          <GridBreak />

          <Grid item xs={largeFieldBreakpoints}>
            {noteField()}
          </Grid>

          <Grid item xs={fullFieldBreakpoints}>
            <ServiceFiles
              files={props.updateSchema.creatingFiles || []}
              setFiles={(files) => props.setUpdateSchema({ ...props.updateSchema, creatingFiles: files })}
            />
          </Grid>
        </Grid>
      </Stack>
    </Card>
  );
};
